import { Button, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Attachment, CompositionSection, QuestionnaireResponseItem, Reference } from '@medplum/fhirtypes';
import { Loading, useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { PageHeader } from '../../components/layout/PageHeader';
import { UploadReviewPanel } from './UploadReviewPanel';

const QUESTIONNAIRE_DISPLAY_NAMES: Record<string, string> = {
  'stop-bang': 'STOP-BANG',
  'epworth-sleepiness-scale': 'Epworth Sleepiness Scale',
};

const UPLOAD_LINK_IDS: Record<string, string> = {
  'stop-bang': 'stopbang-upload',
  'epworth-sleepiness-scale': 'ess-upload',
};

function findAttachment(items: QuestionnaireResponseItem[] | undefined, linkId: string): Attachment | undefined {
  for (const item of items ?? []) {
    if (item.linkId === linkId && item.answer?.[0]?.valueAttachment) {
      return item.answer[0].valueAttachment;
    }
    const nested = findAttachment(item.item, linkId);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

interface QuestionnaireEntry {
  name: string;
  attachment?: Attachment;
}

export function MissingQuestionnairesPage(): JSX.Element {
  const { compositionId } = useParams();
  const [searchParams] = useSearchParams();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [patientReference, setPatientReference] = useState('');

  const names = useMemo(
    () =>
      (searchParams.get('q') ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [searchParams]
  );

  // null = still loading, array = ready
  const [entries, setEntries] = useState<QuestionnaireEntry[] | null>(compositionId && names.length > 0 ? null : []);

  useEffect(() => {
    if (!compositionId || names.length === 0) {
      return undefined;
    }

    let cancelled = false;

    medplum
      .readResource('Composition', compositionId)
      .then(async (composition) => {
        if (cancelled) {
          return;
        }

        setPatientReference(composition.subject?.reference ?? '');

        const qrRefs = (composition.section ?? [])
          .flatMap((section: CompositionSection) => section.entry ?? [])
          .filter((entry: Reference) => entry.reference?.startsWith('QuestionnaireResponse/'));

        const qrs = await Promise.all(
          qrRefs.map((entry: Reference) => {
            const id = (entry.reference ?? '').replace('QuestionnaireResponse/', '');
            return medplum.readResource('QuestionnaireResponse', id);
          })
        );

        // Look up questionnaire IDs for the names we need to check
        const nameToId = new Map<string, string>();
        await Promise.all(
          names.map(async (name) => {
            const q = await medplum.searchOne('Questionnaire', { name });
            if (q?.id) {
              nameToId.set(name, q.id);
            }
          })
        );
        const idToName = new Map([...nameToId].map(([name, id]) => [id, name]));

        // Detect which questionnaire names already have a response
        const answeredNames = new Set<string>();
        for (const qr of qrs) {
          if (qr.questionnaire) {
            const qId = qr.questionnaire.replace('Questionnaire/', '');
            const name = idToName.get(qId);
            if (name) {
              answeredNames.add(name);
            }
          }
        }

        const unansweredNames = names.filter((name) => !answeredNames.has(name));

        const attachmentByName: Record<string, Attachment> = {};
        for (const [name, uploadLinkId] of Object.entries(UPLOAD_LINK_IDS)) {
          for (const qr of qrs) {
            const attachment = findAttachment(qr.item, uploadLinkId);
            if (attachment) {
              attachmentByName[name] = attachment;
              break;
            }
          }
        }

        if (!cancelled) {
          setEntries(unansweredNames.map((name) => ({ name, attachment: attachmentByName[name] })));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
          setEntries([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, compositionId, names]);

  if (entries === null) {
    return <Loading />;
  }

  return (
    <Stack gap="md" p="md">
      <PageHeader
        title="Fill Missing Questionnaires"
        actions={
          <Button variant="subtle" onClick={() => navigate(`/triage/composition/${compositionId}`)}>
            Back to Review
          </Button>
        }
      />

      {entries.length === 0 && <Text c="dimmed">All questionnaires have been filled.</Text>}

      {entries.map((entry) => (
        <UploadReviewPanel
          key={entry.name}
          title={QUESTIONNAIRE_DISPLAY_NAMES[entry.name] ?? entry.name}
          attachment={entry.attachment ?? ({} as Attachment)}
          questionnaireName={entry.name}
          patientReference={patientReference}
          compositionId={compositionId ?? ''}
        />
      ))}
    </Stack>
  );
}
